/**
 * Application constants
 */

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

export const HASURA_URL =
  import.meta.env.VITE_HASURA_URL ||
  'https://evident-phoenix-93.hasura.app/v1/graphql';
export const HASURA_SECRET =
  import.meta.env.VITE_HASURA_SECRET || 'myadminsecret';
export const SHOPID =
  import.meta.env.VITE_SHOPID || '009a211a-5a48-454c-97cf-100080af042c';

export const APP_NAME = 'AR Boutique';

export const ROUTES = {
  HOME: '/',
  PRODUCTS: '/products',
  CATEGORIES: '/categories',
  CART: '/cart',
  ORDERS: '/orders',
  PROFILE: '/profile',
  LOGIN: '/login',
  SIGNUP: '/signup',
  SEARCH: '/search',
  ACCOUNT: '/account',
  CONTACT: '/contact',
  FAQ: '/faq',
  SHIPPING: '/shipping',
  RETURNS: '/returns',
  PRIVACY: '/privacy',
  REELS: '/reels',
} as const;

export const R2_PUBLIC_URL =
  import.meta.env.VITE_R2_PUBLIC_URL ||
  'https://pub-73dd9f9359f04b7eb6bff65894b33c65.r2.dev';

// --- Reels (GraphQL pagination + preload) ---
// Override via `.env`:
// VITE_REELS_FETCH_LIMIT             — items per GraphQL query / batch (default 8)
// VITE_REELS_FETCH_INITIAL_OFFSET    — offset for first query (default 0)
// VITE_REELS_PRELOAD_SLOT            — 1-based; preload next batch when viewer hits this slot in batch (default 6)
// VITE_REELS_DEMO_POOL_SIZE          — offline demo reels when API empty (default 32)

const parsePositiveInt = (raw: string | undefined, fallback: number): number => {
  if (raw === undefined || raw === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
};

const parseNonNegativeInt = (raw: string | undefined, fallback: number): number => {
  if (raw === undefined || raw === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
};

/** GraphQL `limit` for each reels request (`mst_post`). Env: `VITE_REELS_FETCH_LIMIT` (default 8). */
export const REELS_FETCH_LIMIT = parsePositiveInt(
  import.meta.env.VITE_REELS_FETCH_LIMIT,
  8
);

/** GraphQL `offset` for the first reels page. Env: `VITE_REELS_FETCH_INITIAL_OFFSET`. */
export const REELS_FETCH_INITIAL_OFFSET = parseNonNegativeInt(
  import.meta.env.VITE_REELS_FETCH_INITIAL_OFFSET,
  0
);

/**
 * Start preloading when the viewer reaches this 1-based index within each `REELS_FETCH_LIMIT` window (e.g. `6` = sixth reel).
 * Env: `VITE_REELS_PRELOAD_SLOT`.
 */
const REELS_PRELOAD_SLOT_ONE_BASED = parsePositiveInt(
  import.meta.env.VITE_REELS_PRELOAD_SLOT,
  6
);

/**
 * Derived: `activeIndex % REELS_FETCH_LIMIT` must match this value to trigger preloading.
 */
export const REELS_PRELOAD_TRIGGER_INDEX_MOD = Math.min(
  REELS_PRELOAD_SLOT_ONE_BASED - 1,
  Math.max(0, REELS_FETCH_LIMIT - 1)
);

/** Demo/offline reels pool length when GraphQL returns no posts. Env: `VITE_REELS_DEMO_POOL_SIZE`. */
export const REELS_DEMO_POOL_SIZE = parsePositiveInt(
  import.meta.env.VITE_REELS_DEMO_POOL_SIZE,
  32
);
